import React from 'react';
import { motion } from 'framer-motion';
import {
  FaDumbbell,
  FaUsers,
  FaAppleAlt,
  FaFire,
  FaArrowRight,
  FaComments,
  FaInfoCircle
} from 'react-icons/fa';

const servicesData = [
  {
    icon: <FaDumbbell />,
    title: '1-on-1 Personal Training',
    description:
      'Individual coaching with close attention to technique, programming and progress.',
    color: '#e51b24',
  },
  {
    icon: <FaUsers />,
    title: 'Small Group Training',
    description:
      'Coach-led training with the energy of a group and structured programming.',
    color: '#ef233c',
  },
  {
    icon: <FaComments />,
    title: 'Fitness Consultation',
    description:
      'Goal assessment, training direction and personalized recommendations.',
    color: '#3498db',
  },
  {
    icon: <FaAppleAlt />,
    title: 'Nutrition Guidance',
    description:
      'Practical nutrition support aligned with the client’s fitness goal.',
    color: '#27ae60',
  },
  {
    icon: <FaFire />,
    title: 'Transformation Coaching',
    description:
      'Structured training, nutrition, accountability and progress monitoring.',
    color: '#f39c12',
  },
];

const workoutStructure = [
  { step: '01', stage: 'Warm-up', desc: 'General body prep & temperature rise' },
  { step: '02', stage: 'Movement Preparation', desc: 'Joint mobility & muscle activation' },
  { step: '03', stage: 'Main Strength / Skill', desc: 'Primary compound movement & focus' },
  { step: '04', stage: 'Secondary Compound', desc: 'Supporting multi-joint exercises' },
  { step: '05', stage: 'Accessories', desc: 'Targeted hypertrophy & isolation work' },
  { step: '06', stage: 'Conditioning', desc: 'Work capacity & cardio (when required)' },
  { step: '07', stage: 'Cool-down & Mobility', desc: 'Recovery, stretching & heart rate reduction' },
];

const Services = () => {
  const transition = { type: 'spring', duration: 2.5 };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { ...transition, delay: i * 0.1 },
    }),
  };

  return (
    <section className="relative px-4 md:px-12 py-16 text-white overflow-hidden max-w-7xl mx-auto flex flex-col gap-16" id="services">
      {/* Background blurs */}
      <div className="absolute w-72 h-72 bg-red-600/15 blur-[140px] rounded-full top-0 right-0 -z-10" />
      <div className="absolute w-72 h-72 bg-red-800/10 blur-[140px] rounded-full bottom-0 left-0 -z-10" />

      {/* Header */}
      <motion.div
        className="text-center max-w-2xl mx-auto flex flex-col items-center gap-3"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={transition}
        viewport={{ once: true }}
      >
        <span className="inline-block bg-red-600/15 border border-red-500/30 text-red-500 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg">
          Our Premium Services
        </span>
        <div className="text-3xl sm:text-5xl font-black uppercase tracking-wide">
          <span className="text-transparent [-webkit-text-stroke:_1.5px_white]">PREMIUM </span>
          <span className="text-red-500">SERVICES</span>
        </div>
        <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
          Structured coaching, individual programming, and transformation solutions available across all 3 Coimbatore units.
        </p>
      </motion.div>

      {/* Premium Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {servicesData.map((service, i) => (
          <motion.div
            key={i}
            className="group relative bg-gradient-to-b from-[#181a24]/90 to-[#12141c]/90 backdrop-blur-xl p-8 rounded-3xl border border-white/10 flex flex-col justify-between hover:border-red-500/50 transition-all duration-300 shadow-xl overflow-hidden"
            custom={i}
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true, margin: '-40px' }}
            whileHover={{ y: -8, scale: 1.02 }}
          >
            <div>
              <div className="flex items-center justify-between mb-6">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-md transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${service.color}25`, color: service.color }}
                >
                  {service.icon}
                </div>
                <span className="text-[10px] font-black uppercase text-red-400 bg-red-600/10 px-2.5 py-1 rounded-full border border-red-500/20">
                  Service 0{i + 1}
                </span>
              </div>

              <h3 className="text-xl font-black uppercase tracking-wide mb-3 group-hover:text-red-400 transition-colors">
                {service.title}
              </h3>
              <p className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-6">
                {service.description}
              </p>

              <a
                href={`https://wa.me/917339504861?text=${encodeURIComponent(
                  `Hi IDOL Fitness Zone, I would like to inquire about ${service.title}!`
                )}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-red-400 hover:text-white transition-colors mb-4 group-hover:translate-x-1"
              >
                <span>Talk to a Coach</span> <FaArrowRight className="text-[10px]" />
              </a>
            </div>

            {/* Color Accent Bar */}
            <div
              className="h-1 w-full rounded-full transition-all duration-300 group-hover:h-1.5"
              style={{ backgroundColor: service.color }}
            />
          </motion.div>
        ))}
      </div>

      {/* ── SAMPLE WORKOUT STRUCTURE SECTION ── */}
      <motion.div
        className="bg-gradient-to-b from-[#181a24] to-[#11131a] p-6 md:p-10 rounded-3xl border border-red-500/30 shadow-2xl flex flex-col gap-6 relative overflow-hidden"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={transition}
        viewport={{ once: true }}
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent" />

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-white/10 pb-6">
          <div>
            <span className="text-red-500 text-xs font-black uppercase tracking-widest block mb-1">
              Methodical Execution
            </span>
            <h3 className="text-2xl sm:text-4xl font-black uppercase text-white">
              SAMPLE WORKOUT <span className="text-red-500">STRUCTURE</span>
            </h3>
          </div>
          <p className="text-gray-300 text-xs sm:text-sm max-w-md">
            Every session at IDOL Fitness Zone follows a 7-stage workout blueprint to maximize efficiency, safety, and training performance.
          </p>
        </div>

        {/* 7-Stage Flow Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {workoutStructure.map((item, idx) => (
            <div
              key={item.step}
              className="bg-[#0e1017] border border-white/10 hover:border-red-500/40 p-4 rounded-2xl flex flex-col gap-1.5 transition-all group"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-extrabold uppercase text-red-500 bg-red-600/10 px-2 py-0.5 rounded-md border border-red-500/20">
                  Stage {item.step}
                </span>
              </div>
              <h4 className="text-sm font-black uppercase text-white group-hover:text-red-400 transition-colors">
                {item.stage}
              </h4>
              <p className="text-[11px] text-gray-400 leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Disclaimer Note */}
        <div className="bg-[#08090d] border border-white/10 p-4 rounded-2xl flex items-start gap-3 text-xs text-gray-300">
          <FaInfoCircle className="text-red-500 text-lg flex-shrink-0 mt-0.5" />
          <p className="leading-relaxed text-[11px] sm:text-xs text-gray-300">
            <strong className="text-white font-bold">Customization Principle:</strong> The exact exercise selection, sets, repetitions, intensity and progression should be customized rather than presented as a universal prescription.
          </p>
        </div>
      </motion.div>
    </section>
  );
};

export default Services;
